import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import React, { useCallback, useEffect, useRef, useState } from "react";

import Skeleton from "../../patterns/skeleton/Skeleton";
import { Link } from "../link/Link";

export type Breadcrumb = {
  title: string;
  navigate?: () => void;
  href?: string;
};

type BreadcrumbsProps = {
  breadcrumbs: Breadcrumb[];
  isLoading?: boolean;
  testID?: string;
};

export function Breadcrumbs(props: BreadcrumbsProps) {
  const { breadcrumbs, isLoading, testID = "breadcrumbs" } = props;
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const handleCollapse = useCallback(() => {
    if (shouldCollapse()) {
      setIsCollapsed(true);
    }
  }, []);

  useEffect(() => {
    handleCollapse();
  }, [handleCollapse]);

  useEffect(() => {
    if (typeof visualViewport != "undefined") {
      window.visualViewport?.addEventListener("resize", handleCollapse);
    }

    return () =>
      window.visualViewport?.removeEventListener("resize", handleCollapse);
  }, [handleCollapse]);

  const shouldCollapse = () => {
    if (outerRef?.current && innerRef?.current) {
      return outerRef.current.clientWidth <= innerRef.current.clientWidth;
    }
  };
  const handleOnPress = () => {
    setIsCollapsed(false);
  };

  const firstBreadcrumb = breadcrumbs[0];
  const lastBreadcrumb = breadcrumbs.at(-1);

  if (isLoading) {
    return <Skeleton textStyle="global/body-secondary" width={200} />;
  }

  return (
    <div ref={outerRef} className={styles.container} data-testid={testID}>
      <div ref={innerRef} className={css(styles.wrapper)}>
        {isCollapsed ? (
          <>
            <Link
              testID="first-breadcrumb"
              onPress={firstBreadcrumb?.navigate}
              href={firstBreadcrumb?.href}
              textColor="text-weak"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              hasUnderline={
                firstBreadcrumb?.navigate !== undefined &&
                firstBreadcrumb?.href !== undefined
              }
            >
              {firstBreadcrumb?.title}
            </Link>
            <Divider />
            <Ellipsis onPress={handleOnPress} />
            <Divider />
            <Link
              testID="last-breadcrumb"
              onPress={lastBreadcrumb?.navigate}
              href={lastBreadcrumb?.href}
              textColor="text-weak"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              hasUnderline={
                lastBreadcrumb?.navigate !== undefined &&
                lastBreadcrumb?.href !== undefined
              }
            >
              {lastBreadcrumb?.title}
            </Link>
          </>
        ) : (
          <>
            {breadcrumbs
              .slice(0, -1)
              .map(({ title, navigate, href }: Breadcrumb, index: number) => {
                return (
                  <React.Fragment key={index}>
                    <Link
                      testID={`breadcrumb-${slugify(title)}`}
                      onPress={navigate}
                      href={href}
                      textColor="text-weak"
                      textStyle="global/body-secondary"
                      linkStyle="monochrome"
                      hasUnderline={
                        navigate !== undefined && href !== undefined
                      }
                    >
                      {title}
                    </Link>
                    <Divider />
                  </React.Fragment>
                );
              })}
            <Link
              testID="last-breadcrumb"
              key={lastBreadcrumb?.title}
              onPress={lastBreadcrumb?.navigate}
              href={lastBreadcrumb?.href}
              textColor="text-weak"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              hasUnderline={
                lastBreadcrumb?.navigate !== undefined &&
                lastBreadcrumb?.href !== undefined
              }
            >
              {lastBreadcrumb?.title}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

type EllipsisProps = {
  onPress: () => void;
};

function Ellipsis({ onPress }: EllipsisProps) {
  return (
    <div className={styles.ellipsis}>
      <Link
        testID="ellipsis"
        onPress={onPress}
        textColor="text-weak"
        textStyle="global/body-secondary"
        hasUnderline={false}
        linkStyle="monochrome"
      >
        ...
      </Link>
    </div>
  );
}

function Divider() {
  return <>/</>;
}

const styles = {
  container: css({ width: "100%" }),
  wrapper: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    boxSizing: "border-box",
    gap: `${"var(--space-3)"} ${"var(--space-2)"}`,
    width: "fit-content",
  }),
  ellipsis: css({
    display: "flex",
    justifyContent: "center",
    width: "var(--space-10)",
  }),
};
